import { Component, OnInit, PLATFORM_ID, inject } from '@angular/core';
import { DatePipe, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { NgxCurrencyDirective } from 'ngx-currency';
import { PortalCotacaoService } from '../../shared/services/apis/compras/portal-cotacao.service';
import { PortalCotacaoStatusEnum } from '../../shared/enums/compras/portal-cotacao-status.enum';
import { PortalCotacaoDetalheResponse } from '../../shared/services/responses/compras/portal-cotacao-detalhe.response';
import { PortalCotacaoResumoResponse } from '../../shared/services/responses/compras/portal-cotacao-resumo.response';
import { MaskService } from '../../shared/services/mask.service';
import { TipoFreteStruct } from '../../shared/services/structs/compras/tipo-frete.struct';

type PortalState = 'loading' | 'invalid' | 'gate' | 'form' | 'respondida' | 'encerrada' | 'sucesso';

// Página PÚBLICA (fora do shell autenticado) onde o fornecedor responde a cotação.
// Credencial = token do link; barreira secundária = 5 primeiros dígitos do documento.
@Component({
  selector: 'app-portal-cotacao',
  standalone: true,
  imports: [FormsModule, DatePipe, NgxCurrencyDirective],
  templateUrl: './portal-cotacao.component.html',
  styleUrls: [
    '../../shared/styles/portal-preview-shared.css',
    './portal-cotacao.component.css',
  ],
})
export class PortalCotacaoComponent implements OnInit {
  public state: PortalState = 'loading';
  public token = '';
  public documento = '';
  public gateErro = '';
  public abrindo = false;
  public salvando = false;

  public resumo: PortalCotacaoResumoResponse | null = null;
  public detalhe: PortalCotacaoDetalheResponse | null = null;
  public tiposFrete: TipoFreteStruct[] = [];
  // Página pública (sem usuário logado): o MaskService cai na precisão padrão de 2 casas.
  public readonly moedaMask = this.maskService.getDynamicCurrencyMask(null, false);

  // Cabeçalho da proposta (bind template-driven)
  public condicaoPagamento = '';
  public idTipoFrete: number | null = null;
  public valorFrete: number | null = null;
  public validadeDias: number | null = null;
  public observacoes = '';

  // Parcelas da condição de pagamento (mesmo editor do comprador). `meio` é rótulo (não persistido).
  public readonly meioOpcoes = ['Dinheiro', 'Boleto', 'PIX', 'Transferência', 'Cartão de crédito', 'Cheque'];
  public parcelas: { dias: number; data: string | null; meio: string }[] = [];

  private readonly platformId = inject(PLATFORM_ID);

  constructor(
    private route: ActivatedRoute,
    private portalService: PortalCotacaoService,
    private maskService: MaskService
  ) {}

  public ngOnInit(): void {
    // No SSR renderizamos apenas o "loading": a chamada HTTP anônima falharia no
    // servidor e assaria o estado "invalid" no HTML, causando um flash de
    // "Link inválido" antes da hidratação. A busca real acontece só no browser.
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.token = this.route.snapshot.paramMap.get('token') ?? '';
    if (!this.token) {
      this.state = 'invalid';
      return;
    }

    this.portalService.resumo(this.token).subscribe({
      next: res => {
        this.resumo = res;
        if (res.isError || res.status === PortalCotacaoStatusEnum.Invalida) {
          this.state = 'invalid';
          return;
        }
        if (res.status === PortalCotacaoStatusEnum.Encerrada) {
          this.state = 'encerrada';
          return;
        }
        if (res.status === PortalCotacaoStatusEnum.Respondida) {
          this.state = 'respondida';
          return;
        }
        // Pendente: exige documento? mostra o gate; senão abre direto.
        if (res.requerDocumento) {
          this.state = 'gate';
        } else {
          this.abrir();
        }
      },
      error: () => (this.state = 'invalid'),
    });
  }

  public get podeAbrir(): boolean {
    return /^[A-Z0-9]{5}$/i.test(this.documento.trim());
  }

  public abrir(): void {
    if (this.abrindo) return;

    this.gateErro = '';
    if (this.resumo?.requerDocumento && !this.podeAbrir) {
      this.gateErro = 'Informe os 5 primeiros dígitos do documento.';
      return;
    }

    this.abrindo = true;
    this.portalService.abrir(this.token, this.documento).subscribe({
      next: res => {
        this.abrindo = false;
        if (res.isError) {
          this.gateErro = res.errorDescription || 'Não foi possível abrir a cotação.';
          this.state = this.resumo?.requerDocumento ? 'gate' : 'invalid';
          return;
        }
        this.detalhe = res;
        this.condicaoPagamento = res.condicaoPagamento ?? '';
        this.idTipoFrete = res.idTipoFrete ?? null;
        this.valorFrete = res.valorFrete ?? null;
        this.validadeDias = res.validadeDias ?? null;
        this.observacoes = res.observacoes ?? '';
        this.parcelas = (res.parcelas ?? []).map(p => ({
          dias: p.dias ?? 0,
          data: p.dataVencimento ? p.dataVencimento.substring(0, 10) : null,
          meio: this.meioOpcoes[0],
        }));
        this.state = res.status === PortalCotacaoStatusEnum.Encerrada
          ? 'encerrada'
          : res.status === PortalCotacaoStatusEnum.Respondida ? 'respondida' : 'form';
        this.carregarTiposFrete();
      },
      error: () => {
        this.abrindo = false;
        this.gateErro = 'Não foi possível abrir a cotação.';
      },
    });
  }

  private carregarTiposFrete(): void {
    if (!this.isAtivo('campoFrete') || this.tiposFrete.length > 0) return;
    this.portalService.tiposFrete().subscribe({
      next: res => {
        if (!res.isError) this.tiposFrete = res.lista ?? [];
      },
    });
  }

  public isAtivo(chave: string): boolean {
    // Sem configuração de comunicação-cotação salva, camposAtivos vem vazio. Espelha a
    // semântica do documento (planilha/PDF) — "vazio = conjunto padrão completo" — e mostra
    // todos os campos. Sem isso, um fornecedor que responde pelo LINK não teria nem onde
    // informar o preço unitário (campoPrecoUnitario), enquanto quem responde por planilha teria.
    const campos = this.detalhe?.camposAtivos ?? [];
    return campos.length === 0 || campos.includes(chave);
  }

  // Só existe "indisponível" quando o campo está ativo — sem ele, a quantidade
  // sempre reflete o solicitado e não há como o fornecedor sinalizar falta de estoque.
  public isIndisponivel(item: { quantidade: number }): boolean {
    return this.isAtivo('campoDisponibilidade') && item.quantidade <= 0;
  }

  public get heroGradient(): string {
    const d = this.detalhe ?? this.resumo;
    return `linear-gradient(135deg, ${d?.corHeroInicio ?? '#1C1B1F'}, ${d?.corHeroFim ?? '#2D2A3A'})`;
  }

  public get corMarca(): string {
    return (this.detalhe ?? this.resumo)?.corMarca ?? '#B73232';
  }

  // ----- Parcelas (condição de pagamento) — mesmo comportamento do comprador -----
  public gerarParcelas(): void {
    const dias = this.parseParcelasDias(this.condicaoPagamento);
    if (dias.length === 0) return;
    this.parcelas = dias.map(d => ({ dias: d, data: this.dataEmDias(d), meio: this.meioOpcoes[0] }));
  }

  public addParcela(): void {
    const ultimo = this.parcelas.length ? this.parcelas[this.parcelas.length - 1].dias + 30 : 30;
    this.parcelas = [...this.parcelas, { dias: ultimo, data: this.dataEmDias(ultimo), meio: this.meioOpcoes[0] }];
  }

  public removeParcela(idx: number): void {
    this.parcelas = this.parcelas.filter((_, i) => i !== idx);
  }

  private parseParcelasDias(expr: string): number[] {
    const s = (expr || '').trim();
    if (!s) return [];
    const x = s.match(/^(\d+)\s*x$/i);
    if (x) {
      const n = Math.max(1, Math.min(36, parseInt(x[1], 10)));
      return Array.from({ length: n }, (_, i) => 30 * (i + 1));
    }
    const plus = s.match(/^(\d+)\s*\+\s*(\d+)\s*x$/i);
    if (plus) {
      const first = parseInt(plus[1], 10);
      const extra = parseInt(plus[2], 10);
      const dias = [first];
      for (let i = 1; i <= extra; i++) dias.push(first + 30 * i);
      return dias;
    }
    return s.split(/[\s,;/]+/).map(t => parseInt(t, 10)).filter(n => !isNaN(n));
  }

  private dataEmDias(dias: number): string {
    const d = new Date();
    d.setDate(d.getDate() + (dias || 0));
    return d.toISOString().substring(0, 10);
  }

  public toggleAlternativa(item: { alternativaAberta?: boolean }): void {
    item.alternativaAberta = !item.alternativaAberta;
  }

  public enviar(): void {
    if (!this.detalhe) return;
    this.salvando = true;

    const body = {
      documento: this.documento,
      condicaoPagamento: this.isAtivo('campoCondicaoPagamento') ? this.condicaoPagamento || null : null,
      idTipoFrete: this.isAtivo('campoFrete') ? this.idTipoFrete : null,
      valorFrete: this.isAtivo('campoFrete') ? this.valorFrete : null,
      validadeDias: this.isAtivo('campoValidadeProposta') ? this.validadeDias : null,
      observacoes: this.observacoes || null,
      parcelas: this.isAtivo('campoCondicaoPagamento')
        ? this.parcelas.map((p, i) => ({ sequencia: i + 1, dias: p.dias ?? null, dataVencimento: p.data || null, valor: null }))
        : null,
      itens: this.detalhe.itens.map(i => ({
        idCompraItem: i.idCompraItem,
        valorUnitario: i.valorUnitario,
        // Produto cadastrado: envia a marca do catálogo (não perguntada); avulso: marca ofertada.
        marcaOfertada: i.marcaProduto ? i.marcaProduto : i.marcaOfertada,
        prazoEntregaDias: i.prazoEntregaDias,
        // Unidades disponíveis (0 = sem estoque) — quando o campo não está ativo,
        // mantém a quantidade solicitada (fornecedor não opina sobre disponibilidade).
        quantidade: this.isAtivo('campoDisponibilidade') ? i.quantidade : i.quantidadeSolicitada,
        valorIpi: this.isAtivo('campoIpi') ? i.valorIpi : null,
        valorIcmsSt: this.isAtivo('campoSt') ? i.valorIcmsSt : null,
        observacoes: i.observacoes,
        alternativaDescricao: i.alternativaAberta ? (i.alternativaDescricao || null) : null,
        alternativaMarca: i.alternativaAberta ? (i.alternativaMarca || null) : null,
        alternativaValorUnitario: i.alternativaAberta ? (i.alternativaValorUnitario ?? null) : null,
        alternativaValorIpi: i.alternativaAberta && this.isAtivo('campoIpi') ? (i.alternativaValorIpi ?? null) : null,
        alternativaValorIcmsSt: i.alternativaAberta && this.isAtivo('campoSt') ? (i.alternativaValorIcmsSt ?? null) : null,
        alternativaQuantidade: i.alternativaAberta && this.isAtivo('campoDisponibilidade') ? (i.alternativaQuantidade ?? null) : null,
        alternativaPrazoEntregaDias: i.alternativaAberta ? (i.alternativaPrazoEntregaDias ?? null) : null,
      })),
    };

    this.portalService.responder(this.token, body).subscribe({
      next: res => {
        this.salvando = false;
        if (res.isError) {
          this.gateErro = res.errorDescription || 'Não foi possível enviar a proposta.';
          return;
        }
        this.state = 'sucesso';
      },
      error: () => {
        this.salvando = false;
        this.gateErro = 'Não foi possível enviar a proposta.';
      },
    });
  }
}
